import React, { useRef, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import $ from "jquery";
import moment from "moment";
import DataTable from "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import "daterangepicker/daterangepicker.css";
import "daterangepicker";
import "select2";
import "select2/dist/css/select2.min.css";
import { claimPortalDashboardApi } from "../services/claimPortalDashboardApi";
import { seDashboardAPI } from "../services/seDashboardApi";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import DashboardExportDropdown from "../components/common/DashboardExportDropdown";
import { getClaimsFromToken } from "../utils/jwtDecoder";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";
import "./Dashboard.css";
import "./ClaimPortalDashboard.css";

// Plugin to display each segment's value inside its colored arc (skip 0)
const pieChartValuePlugin = {
  id: "pieChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    if (chart.config.type !== "pie" && chart.config.type !== "doughnut") return;
    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((element, index) => {
      const value = data[index];
      if (value === 0 || value == null) return;
      const position = element.tooltipPosition();
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = "bold 13px Arial";
      ctx.fillStyle = "#000000";
      // For charts that keep "display values" separate from dataset values, allow override.
      const displayValues = chart?.config?.options?.plugins?.pieChartValuePlugin?.displayValues;
      const display = Array.isArray(displayValues) ? displayValues[index] : value;
      if (display === 0 || display == null) {
        ctx.restore();
        return;
      }
      ctx.fillText(String(display), position.x, position.y);
      ctx.restore();
    });
  },
};

// Plugin to display value labels above each bar
const barChartValuePlugin = {
  id: "barChartValuePlugin",
  afterDatasetsDraw: (chart) => {
    if (chart.config.type !== "bar") return;
    const ctx = chart.ctx;
    const data = chart.data.datasets[0].data;
    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((element, index) => {
      const value = data[index];
      if (value === null || value === undefined) return;
      const { x } = element.tooltipPosition();
      const barTop = element.y !== undefined ? element.y : element.base - Math.abs(element.height);
      ctx.save();
      ctx.fillStyle = "#333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      ctx.fillText(value, x, barTop - 4);
      ctx.restore();
    });
  },
};

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  pieChartValuePlugin,
  barChartValuePlugin
);

const tatClass = (v) => {
  const n = Number(v || 0);
  if (n === 0) return "tat-0";
  if (n <= 10) return "tat-low";
  if (n <= 50) return "tat-mid";
  return "tat-high";
};

const statusClass = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "APPROVED") return "status-approved";
  if (v === "PENDING") return "status-pending";
  if (v === "CLOSED") return "status-closed";
  return "status-closed";
};

// Child SR format fallback: SR/FY26/D/QC/0002-001 -> SR/FY26/D/0002
const deriveParentFromChildSr = (child) => {
  const raw = String(child || "").trim();
  if (!raw || !raw.includes("/") || !raw.includes("-")) return "";
  const parts = raw.split("/").filter(Boolean);
  if (parts.length < 4) return "";
  const last = parts[parts.length - 1] || "";
  const baseNo = last.split("-")[0] || "";
  if (!baseNo) return "";
  return `${parts[0]}/${parts[1]}/${parts[2]}/${baseNo}`;
};

const ClaimPortalDashboard = () => {
  const navigate = useNavigate();
  // Refs for daterangepicker and select2 dropdowns (copied from /dashboard)
  const filterDateRangePickerRef = useRef(null);
  const statusSelectRef = useRef(null);
  const bvSelectRef = useRef(null);
  const countrySelectRef = useRef(null);
  const zoneSelectRef = useRef(null);
  const regionSelectRef = useRef(null);
  const territorySelectRef = useRef(null);
  const complaintTableRef = useRef(null);
  const complaintDtRef = useRef(null);

  // Current financial year range: 01-Apr to 31-Mar (same as /dashboard)
  const getCurrentFinancialYearRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0 = Jan, 3 = Apr
    const fyStartYear = month < 3 ? year - 1 : year;
    const fyEndYear = month < 3 ? year : year + 1;
    const start = `${fyStartYear}-04-01`;
    const end = `${fyEndYear}-03-31`;
    const fmt = (y, m, d) => {
      const dd = String(d).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      return `${dd}-${mm}-${y}`;
    };
    const displayText = `${fmt(fyStartYear, 4, 1)} - ${fmt(fyEndYear, 3, 31)}`;
    return { dateRangeStart: start, dateRangeEnd: end, dateRange: displayText };
  };

  const defaultFY = getCurrentFinancialYearRange();

  // Filter states (replicated from /dashboard)
  const [filters, setFilters] = useState({
    dateRange: defaultFY.dateRange,
    dateRangeStart: defaultFY.dateRangeStart,
    dateRangeEnd: defaultFY.dateRangeEnd,
    status: [],
    bv: [],
    invoiceNo: "",
    country: "",
    zone: "",
    region: "",
    territory: "",
  });

  const [statusOptions, setStatusOptions] = useState([]);
  const [bvOptions, setBvOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [territoryOptions, setTerritoryOptions] = useState([]);
  const [invoiceSuggestions, setInvoiceSuggestions] = useState([]);
  const [showInvoiceSuggestions, setShowInvoiceSuggestions] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  const [kpis, setKpis] = useState({
    totalClaims: 0,
    approvedClaims: 0,
    pendingClaims: 0,
    closedClaims: 0,
    avgGrandTatDays: 0,
  });

  const [complaintRows, setComplaintRows] = useState([]);
  const [userTypeRows, setUserTypeRows] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);

  // Export helpers (Excel)
  const formatDateDDMMYYYY = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${d.getFullYear()}`;
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportJsonToExcel = async (rows, filename, sheetName = "Export") => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet(sheetName);
    const headers = Object.keys(rows[0]);

    ws.addRow(headers);
    ws.getRow(1).font = { bold: true };
    ws.getRow(1).alignment = { vertical: "middle" };

    rows.forEach((r) => {
      ws.addRow(headers.map((h) => (r[h] == null ? "" : r[h])));
    });

    headers.forEach((h, idx) => {
      const col = ws.getColumn(idx + 1);
      let max = Math.max(String(h).length, 10);
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value == null ? "" : String(cell.value);
        max = Math.max(max, v.length);
      });
      col.width = Math.min(Math.max(max + 2, 10), 45);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename
    );
  };

  const getComplaintRowValue = (r, k) => {
    if (!r || !k) return null;
    if (Object.prototype.hasOwnProperty.call(r, k)) return r[k];
    const pascal = k[0].toUpperCase() + k.slice(1);
    if (Object.prototype.hasOwnProperty.call(r, pascal)) return r[pascal];
    const target = String(k).toLowerCase();
    const key = Object.keys(r).find((x) => String(x).toLowerCase() === target);
    return key ? r[key] : null;
  };

  const getComplaintExportRows = () =>
    // Export what the grid is actually showing: when a search is active only the
    // matching rows are written out, otherwise the full data set.
    getDataTableExportRows(
      complaintDtRef,
      Array.isArray(complaintRows) ? complaintRows : []
    ).map((r) => ({
      "Sr No": getComplaintRowValue(r, "complaintNo") || "",
      FY: getComplaintRowValue(r, "financialYear") || "",
      Status: getComplaintRowValue(r, "status") || "",
      Plant: getComplaintRowValue(r, "plant") || "",
      Zone: getComplaintRowValue(r, "zone") || "",
      "Business Vertical": getComplaintRowValue(r, "category") || "",
      "Sr Category": getComplaintRowValue(r, "subCategory") || "",
      "TAT SE": getComplaintRowValue(r, "tatSe") ?? 0,
      "TAT BCM": getComplaintRowValue(r, "tatBcm") ?? 0,
      "TAT Zonal Head": getComplaintRowValue(r, "tatZonalHead") ?? 0,
      "TAT Dispatch": getComplaintRowValue(r, "tatDispatch") ?? 0,
      "TAT CPD Head": getComplaintRowValue(r, "tatCpdHead") ?? 0,
      "TAT Quality": getComplaintRowValue(r, "tatQuality") ?? 0,
      "TAT Mfg. Head": getComplaintRowValue(r, "tatMfgHead") ?? 0,
      "TAT Country Head": getComplaintRowValue(r, "tatCountryHead") ?? 0,
      "TAT MD": getComplaintRowValue(r, "tatMd") ?? 0,
      "TAT Export Comm.": getComplaintRowValue(r, "tatExportComm") ?? 0,
      "TAT Exp. Contr.": getComplaintRowValue(r, "tatExportContr") ?? 0,
      "TAT Grand Total": getComplaintRowValue(r, "tatGrandTotal") ?? 0,
    }));

  const handleExportComplaintsExcel = async () => {
    const exportRows = getComplaintExportRows();
    await exportJsonToExcel(
      exportRows,
      `claim-portal-complaints-${formatDateDDMMYYYY()}.xlsx`,
      "Claims"
    );
  };

  const handleExportComplaintsCsv = () => {
    const exportRows = getComplaintExportRows();
    if (!exportRows.length) return;
    const headers = Object.keys(exportRows[0]);
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.map(escape).join(","),
      ...exportRows.map((row) => headers.map((h) => escape(row[h])).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, `claim-portal-complaints-${formatDateDDMMYYYY()}.csv`);
  };

  const handleExportComplaintsPdf = () => {
    const exportRows = getComplaintExportRows();
    if (!exportRows.length) return;
    const headers = Object.keys(exportRows[0]);
    const body = exportRows.map((row) => headers.map((h) => row[h]));
    const doc = new jsPDF("landscape");
    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [245, 247, 251], textColor: [45, 47, 51] },
      margin: { top: 12, left: 8, right: 8 },
    });
    doc.save(`claim-portal-complaints-${formatDateDDMMYYYY()}.pdf`);
  };

  const exportRowsToCsv = (rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      headers.map(escape).join(","),
      ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, filename);
  };

  const exportRowsToPdf = (rows, filename, orientation = "portrait") => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const body = rows.map((row) => headers.map((h) => row[h]));
    const doc = new jsPDF(orientation);
    autoTable(doc, {
      head: [headers],
      body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [245, 247, 251], textColor: [45, 47, 51] },
      margin: { top: 12, left: 10, right: 10 },
    });
    doc.save(filename);
  };

  // Helper function to get financial year start (April 1st) (copied from /dashboard)
  const getFinancialYearStart = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < 3) {
      return new Date(year - 1, 3, 1);
    }
    return new Date(year, 3, 1);
  };

  // Helper function to get quarter start (copied from /dashboard)
  const getQuarterStart = (date) => {
    const month = date.getMonth();
    const quarter = Math.floor(month / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
  };

  // Helper function to get week start (Monday) (copied from /dashboard)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  // Load Status options (same API + mapping as /dashboard)
  const loadStatusOptions = async () => {
    try {
      const response = await seDashboardAPI.getSRStatuses(true);
      const ok = response?.success === true || response?.Success === true;
      const list = response?.data ?? response?.Data;
      if (ok && Array.isArray(list)) {
        const options = list.map((s) => ({
          id: s.srStatusCode || s.sRStatusCode || s.SRStatusCode || "",
          text: s.name || s.Name || "",
        }));
        setStatusOptions(options);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading status options:", e);
    }
  };

  // Load Product Vertical options (same API + mapping as /dashboard)
  const loadProductVerticalOptions = async () => {
    try {
      const response = await seDashboardAPI.getProductVerticalOptions({
        isActive: true,
        pageNumber: 1,
        pageSize: 500,
      });
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && payload) {
        const list = payload.categories ?? payload.Categories ?? [];
        const options = list.map((c) => ({
          id: c.catgCode || c.CatgCode,
          text: c.catgName || c.CatgName || c.catgCode || c.CatgCode,
        }));
        setBvOptions(options);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading Product Vertical (category) options:", e);
    }
  };

  // Load Country options (same API + mapping as /dashboard)
  const loadCountryOptions = async () => {
    try {
      const response = await seDashboardAPI.getCountries();
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && Array.isArray(payload)) {
        const options = payload.map((country) => ({
          id: country.countryCode || country.CountryCode,
          text: country.countryName || country.CountryName,
        }));
        setCountryOptions(options);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading Country options:", e);
    }
  };

  // Load Zone options (cascading) (same API + mapping as /dashboard)
  const loadZoneOptions = async (countryCode) => {
    try {
      const response = await seDashboardAPI.getZones(countryCode || undefined);
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && Array.isArray(payload)) {
        const options = payload.map((zone) => ({
          id: zone.zoneCode || zone.ZoneCode,
          text: zone.zoneName || zone.ZoneName,
          countryCode: zone.countryCode || zone.CountryCode,
        }));
        setZoneOptions(options);
      } else {
        setZoneOptions([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading Zone options:", e);
      setZoneOptions([]);
    }
  };

  // Load Region options (cascading) (same API + mapping as /dashboard)
  const loadRegionOptions = async (zoneCode, countryCode) => {
    try {
      const response = await seDashboardAPI.getRegions(
        countryCode || undefined,
        zoneCode || undefined
      );
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && Array.isArray(payload)) {
        const options = payload.map((region) => ({
          id: region.regionCode || region.RegionCode,
          text: region.regionName || region.RegionName,
          zoneCode: region.zoneCode || region.ZoneCode,
        }));
        setRegionOptions(options);
      } else {
        setRegionOptions([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading Region options:", e);
      setRegionOptions([]);
    }
  };

  // Load Territory options (cascading) (same API + mapping as /dashboard)
  const loadTerritoryOptions = async (regionCode, zoneCode, countryCode) => {
    try {
      const response = await seDashboardAPI.getTerritories(
        countryCode || undefined,
        zoneCode || undefined,
        regionCode || undefined
      );
      const successFlag = response?.success ?? response?.Success;
      const payload = response?.data ?? response?.Data;
      if (successFlag && Array.isArray(payload)) {
        const options = payload.map((territory) => ({
          id: territory.territoryCode || territory.TerritoryCode,
          text: territory.territoryName || territory.TerritoryName,
          regionCode: territory.regionCode || territory.RegionCode,
        }));
        setTerritoryOptions(options);
      } else {
        setTerritoryOptions([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading Territory options:", e);
      setTerritoryOptions([]);
    }
  };

  // Load invoice suggestions (copied from /dashboard - mock)
  const loadInvoiceSuggestions = async (searchText) => {
    if (searchText.length < 4) {
      setInvoiceSuggestions([]);
      setShowInvoiceSuggestions(false);
      return;
    }
    setInvoiceSuggestions([searchText + "001", searchText + "002", searchText + "003"]);
    setShowInvoiceSuggestions(true);
  };

  const handleInvoiceInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setFilters((prev) => ({ ...prev, invoiceNo: value }));
    if (value.length >= 4) {
      loadInvoiceSuggestions(value);
    } else {
      setShowInvoiceSuggestions(false);
    }
  };

  const handleInvoiceSuggestionClick = (suggestion) => {
    setFilters((prev) => ({ ...prev, invoiceNo: suggestion }));
    setShowInvoiceSuggestions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showInvoiceSuggestions &&
        !event.target.closest(".invoice-suggestions-list") &&
        !event.target.closest('input[placeholder="Enter invoice number"]')
      ) {
        setShowInvoiceSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showInvoiceSuggestions]);

  // Claim portal data reload (only endpoint adapted; filter params shaped like /dashboard)
  const loadData = React.useCallback(async (overrideParams = {}) => {
    try {
      // IMPORTANT: React renders the table DOM, while DataTables mutates it.
      // Destroy DataTable BEFORE updating rows to avoid React attempting to remove
      // nodes that DataTables already moved/removed (NotFoundError: removeChild).
      if (complaintDtRef.current) {
        try {
          complaintDtRef.current.destroy();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("Claim portal DataTable pre-destroy warning:", e);
        }
        complaintDtRef.current = null;
      }

      const params = {};
      // Same createdBy resolution logic as /csrp/dashboard
      let createdBy = null;
      const u = localStorage.getItem("user");
      if (u) {
        const user = JSON.parse(u);
        createdBy = user.userIdCode || user.UserIdCode || null;
      }
      // Fallback: if user object isn't available, derive from token claims
      if (!createdBy) {
        const token = localStorage.getItem("token");
        if (token) {
          const claims = getClaimsFromToken(token);
          createdBy = claims?.userId || null;
        }
      }
      if (createdBy) params.createdBy = createdBy;

      const fromDate =
        overrideParams.fromDate !== undefined
          ? overrideParams.fromDate
          : filters.dateRangeStart;
      const toDate =
        overrideParams.toDate !== undefined ? overrideParams.toDate : filters.dateRangeEnd;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;

      const statusList = overrideParams.status !== undefined ? overrideParams.status : filters.status;
      const bvList = overrideParams.bv !== undefined ? overrideParams.bv : filters.bv;
      const countryVal = overrideParams.country !== undefined ? overrideParams.country : filters.country;
      const zoneVal = overrideParams.zone !== undefined ? overrideParams.zone : filters.zone;
      const regionVal = overrideParams.region !== undefined ? overrideParams.region : filters.region;
      const territoryVal =
        overrideParams.territory !== undefined ? overrideParams.territory : filters.territory;
      const invoiceVal =
        overrideParams.invoiceNo !== undefined ? overrideParams.invoiceNo : filters.invoiceNo;

      if (invoiceVal && String(invoiceVal).trim()) {
        params.invoiceNumber = String(invoiceVal).trim();
      }
      if (Array.isArray(statusList) && statusList.length > 0) {
        const joinedStatus = statusList
          .map((v) => (typeof v === "object" && v !== null ? v.id ?? v.value ?? "" : v))
          .map((v) => String(v ?? "").trim())
          .filter(Boolean)
          .join(",");
        if (joinedStatus) params.statusCode = joinedStatus;
      }
      if (Array.isArray(bvList) && bvList.length > 0) {
        const joinedBv = bvList
          .map((v) => (typeof v === "object" && v !== null ? v.id ?? v.value ?? "" : v))
          .map((v) => String(v ?? "").trim())
          .filter(Boolean)
          .join(",");
        if (joinedBv) params.productVerticalCode = joinedBv;
      }
      if (countryVal) params.countryCode = countryVal;
      if (zoneVal) params.zoneCode = zoneVal;
      if (regionVal) params.regionCode = regionVal;
      if (territoryVal) params.territoryCode = territoryVal;

      const resp = await claimPortalDashboardApi.getDashboardData(params);
      const ok = resp?.success === true || resp?.Success === true;
      const payload = resp?.data ?? resp?.Data ?? null;
      if (!ok || !payload) return;

      const k = payload.kpis ?? payload.Kpis ?? {};
      setKpis({
        totalClaims: k.totalClaims ?? k.TotalClaims ?? 0,
        approvedClaims: k.approvedClaims ?? k.ApprovedClaims ?? 0,
        pendingClaims: k.pendingClaims ?? k.PendingClaims ?? 0,
        closedClaims: k.closedClaims ?? k.ClosedClaims ?? 0,
        avgGrandTatDays: k.avgGrandTatDays ?? k.AvgGrandTatDays ?? 0,
      });

      const rows = payload.complaintRows ?? payload.ComplaintRows ?? [];
      setComplaintRows(rows);

      // Backward compatible: if API still returns old `zoneRows`, reuse for user type bar.
      const utRows =
        payload.userTypeRows ??
        payload.UserTypeRows ??
        (payload.zoneRows ?? payload.ZoneRows ?? []).map((r) => ({
          userType: r.userType ?? r.UserType ?? r.category ?? r.Category ?? "—",
          avgTatDays: r.avgTatDays ?? r.AvgTatDays ?? 0,
          claimCount: r.claimCount ?? r.ClaimCount ?? 0,
        }));
      setUserTypeRows(Array.isArray(utRows) ? utRows : []);

      // Preferred: API-provided `categoryRows`. Fallback: aggregate from complaint rows so chart isn't blank.
      const apiCatRows = payload.categoryRows ?? payload.CategoryRows;
      if (Array.isArray(apiCatRows) && apiCatRows.length > 0) {
        setCategoryRows(apiCatRows);
      } else if (Array.isArray(rows) && rows.length > 0) {
        const group = new Map();
        rows.forEach((r) => {
          const cat = getComplaintRowValue(r, "category") || "—";
          const tat = Number(getComplaintRowValue(r, "tatGrandTotal") ?? 0);
          const cur = group.get(cat) || { category: cat, sumTat: 0, count: 0 };
          cur.sumTat += tat;
          cur.count += 1;
          group.set(cat, cur);
        });
        const derived = Array.from(group.values()).map((g) => ({
          category: g.category,
          avgTatDays: g.count ? g.sumTat / g.count : 0,
          claimCount: g.count,
        }));
        setCategoryRows(derived);
      } else {
        setCategoryRows([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Error loading claim portal dashboard data:", e);
    }
  }, [
    filters.dateRangeStart,
    filters.dateRangeEnd,
    filters.status,
    filters.bv,
    filters.country,
    filters.zone,
    filters.region,
    filters.territory,
    filters.invoiceNo,
  ]);

  // Initial load: dropdowns + data (same as /dashboard)
  useEffect(() => {
    loadStatusOptions();
    loadProductVerticalOptions();
    loadCountryOptions();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Complaint table: DataTables (sort/search/paging like reference)
  useEffect(() => {
    const el = complaintTableRef.current;
    if (!el) return undefined;

    // If no rows, tear down DataTable so it doesn't parse the empty-state row.
    if (!Array.isArray(complaintRows) || complaintRows.length === 0) {
      if (complaintDtRef.current) {
        complaintDtRef.current.destroy();
        complaintDtRef.current = null;
      }
      return undefined;
    }

    const timer = setTimeout(() => {
      try {
        if (!$.fn.DataTable) DataTable($);

        if (complaintDtRef.current) {
          complaintDtRef.current.destroy();
          complaintDtRef.current = null;
        }

        complaintDtRef.current = $(el).DataTable({
          paging: true,
          searching: true,
          info: true,
          lengthChange: true,
          ordering: true,
          order: [],
          destroy: true,
          scrollX: true,
          autoWidth: false,
          dom: "lfrtip",
          language: { lengthMenu: "_MENU_ entries per page" },
          columnDefs: [
            {
              targets: Array.from({ length: 12 }, (_, i) => i + 8), // TAT columns
              className: "text-center",
            },
          ],
          initComplete: function () {
            try {
              const wrapper = $(el).closest(".dataTables_wrapper, .dt-container");
              if (wrapper.length > 0) {
                const lengthEl = wrapper.find(".dt-length, .dataTables_length");
                const searchEl = wrapper.find(".dt-search, .dataTables_filter");
                if (lengthEl.length > 0 || searchEl.length > 0) {
                  let topWrap = wrapper.find(".dtFltrTop");
                  if (topWrap.length === 0) {
                    topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                    wrapper.prepend(topWrap);
                  }
                  lengthEl.each(function () {
                    $(this).appendTo(topWrap);
                  });
                  // Put Search + kebab together on far-right
                  let rightWrap = topWrap.find(".cpdDtRight");
                  if (rightWrap.length === 0) {
                    rightWrap = $("<div class='cpdDtRight d-flex align-items-center ms-auto'></div>");
                    topWrap.append(rightWrap);
                  }
                  searchEl.each(function () {
                    $(this).appendTo(rightWrap);
                  });

                  // Export dropdown AFTER search box (hover/click menu: Excel/PDF/CSV)
                  const exportId = "cpd-complaints-export";
                  if (rightWrap.find(`#${exportId}`).length === 0) {
                    const exportMenu = $(`
                      <div class="cpd-export-wrap ms-2" id="${exportId}">
                        <button class="export-button cpd-export-trigger" type="button" title="Export">
                          <svg
                            class="export-icon"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                          <span>Export</span>
                          <span class="cpd-export-caret">▾</span>
                        </button>
                        <ul class="cpd-export-menu">
                          <li><button type="button" class="dropdown-item cpd-export-complaints-excel">Excel</button></li>
                          <li><button type="button" class="dropdown-item cpd-export-complaints-pdf">PDF</button></li>
                          <li><button type="button" class="dropdown-item cpd-export-complaints-csv">CSV</button></li>
                        </ul>
                      </div>
                    `);
                    rightWrap.append(exportMenu);

                    exportMenu
                      .find(".cpd-export-complaints-excel")
                      .off("click")
                      .on("click", async () => {
                        await handleExportComplaintsExcel();
                      });
                    exportMenu
                      .find(".cpd-export-complaints-pdf")
                      .off("click")
                      .on("click", () => {
                        handleExportComplaintsPdf();
                      });
                    exportMenu
                      .find(".cpd-export-complaints-csv")
                      .off("click")
                      .on("click", () => {
                        handleExportComplaintsCsv();
                      });
                  }
                }

                const infoEl = wrapper.find(".dt-info, .dataTables_info");
                const pagingEl = wrapper.find(".dt-paging, .dataTables_paginate");
                if (infoEl.length > 0 || pagingEl.length > 0) {
                  let bottomWrap = wrapper.find(".dtFltrBotm");
                  if (bottomWrap.length === 0) {
                    bottomWrap = $("<div class='dtFltrBotm d-flex flex-wrap'></div>");
                    wrapper.append(bottomWrap);
                  }
                  infoEl.each(function () {
                    $(this).appendTo(bottomWrap);
                  });
                  pagingEl.each(function () {
                    $(this).appendTo(bottomWrap);
                  });
                }
              }
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error("Claim portal DataTable initComplete error:", err);
            }
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Claim portal DataTable init error:", err);
      }
    }, 0);

    return () => {
      clearTimeout(timer);
      if (complaintDtRef.current) {
        complaintDtRef.current.destroy();
        complaintDtRef.current = null;
      }
    };
  }, [complaintRows]);

  // SR link click handler (match /csrp/complaints behavior)
  useEffect(() => {
    const el = complaintTableRef.current;
    if (!el) return undefined;

    const $table = $(el);
    $table.off("click", ".sr-link");
    $table.on("click", ".sr-link", function (e) {
      e.preventDefault();
      const parentSrCode = $(this).data("parentSrCode");
      const childSrCode = $(this).data("childSrCode");
      if (!parentSrCode && !childSrCode) return;
      const parent = parentSrCode || childSrCode;
      const query = childSrCode ? `?childSrCode=${encodeURIComponent(childSrCode)}` : "";
      navigate(`/complaints/${encodeURIComponent(parent)}${query}`);
    });

    return () => {
      $table.off("click", ".sr-link");
    };
  }, [navigate, complaintRows]);

  // Load zones when country changes (same as /dashboard)
  useEffect(() => {
    if (filters.country) {
      loadZoneOptions(filters.country);
    } else {
      setZoneOptions([]);
      setFilters((prev) => ({ ...prev, zone: "", region: "", territory: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.country]);

  // Load regions when zone changes (same as /dashboard)
  useEffect(() => {
    if (filters.zone) {
      loadRegionOptions(filters.zone, filters.country);
    } else {
      setRegionOptions([]);
      setFilters((prev) => ({ ...prev, region: "", territory: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.zone, filters.country]);

  // Load territories when region changes (same as /dashboard)
  useEffect(() => {
    if (filters.region) {
      loadTerritoryOptions(filters.region, filters.zone, filters.country);
    } else {
      setTerritoryOptions([]);
      setFilters((prev) => ({ ...prev, territory: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.zone, filters.country]);

  // Initialize filter date range picker (copied from /dashboard)
  useEffect(() => {
    if (!filterExpanded || !filterDateRangePickerRef.current) return;

    const timer = setTimeout(() => {
      const $input = $(filterDateRangePickerRef.current);
      if (!$input.length) return;

      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").remove();
      }

      const today = moment();
      const yesterday = moment().subtract(1, "days");
      const thisWeekStart = getWeekStart(new Date());
      const lastWeekStart = moment(thisWeekStart).subtract(7, "days");
      const lastWeekEnd = moment(thisWeekStart).subtract(1, "days");
      const thisMonthStart = moment().startOf("month");
      const lastMonthStart = moment().subtract(1, "month").startOf("month");
      const lastMonthEnd = moment().subtract(1, "month").endOf("month");
      const thisQuarterStart = getQuarterStart(new Date());
      const lastQuarterStart = moment(thisQuarterStart).subtract(3, "months");
      const lastQuarterEnd = moment(thisQuarterStart).subtract(1, "days");
      const thisFYStart = getFinancialYearStart(new Date());
      const thisFYEnd = moment(thisFYStart).add(1, "year").subtract(1, "day");
      const lastFYStart = moment(thisFYStart).subtract(1, "year");
      const lastFYEnd = moment(thisFYStart).subtract(1, "days");

      $input.daterangepicker({
        autoUpdateInput: false,
        locale: {
          cancelLabel: "Clear",
          applyLabel: "Apply",
          format: "DD-MM-YYYY",
        },
        opens: "left",
        drops: "down",
        showDropdowns: true,
        ranges: {
          Today: [today, today],
          Yesterday: [yesterday, yesterday],
          "This Week": [moment(thisWeekStart), today],
          "Last Week": [lastWeekStart, lastWeekEnd],
          "This Month": [thisMonthStart, today],
          "Last Month": [lastMonthStart, lastMonthEnd],
          "This Quarter": [moment(thisQuarterStart), today],
          "Last Quarter": [lastQuarterStart, lastQuarterEnd],
          "This Financial Year": [moment(thisFYStart), thisFYEnd],
          "Last Financial Year": [lastFYStart, lastFYEnd],
        },
      });

      if (filters.dateRange) {
        $input.val(filters.dateRange);
      }

      $input.on("apply.daterangepicker", function (ev, picker) {
        const displayText =
          picker.startDate.format("DD-MM-YYYY") + " - " + picker.endDate.format("DD-MM-YYYY");

        setFilters((prev) => ({
          ...prev,
          dateRange: displayText,
          dateRangeStart: picker.startDate.format("YYYY-MM-DD"),
          dateRangeEnd: picker.endDate.format("YYYY-MM-DD"),
        }));
        $(this).val(displayText);
      });

      $input.on("cancel.daterangepicker", function () {
        setFilters((prev) => ({ ...prev, dateRange: "" }));
        $(this).val("");
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (filterDateRangePickerRef.current) {
        const $input = $(filterDateRangePickerRef.current);
        if ($input.data("daterangepicker")) {
          $input.off("apply.daterangepicker");
          $input.off("cancel.daterangepicker");
          $input.data("daterangepicker").remove();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterExpanded]);

  // Initialize Select2 for Country (copied from /dashboard)
  useEffect(() => {
    if (!filterExpanded || !countrySelectRef.current || countryOptions.length === 0) return;
    const timer = setTimeout(() => {
      const $select = $(countrySelectRef.current);
      if (!$select.length) return;

      if ($select.data("select2")) {
        $select.select2("destroy");
      }

      $select.empty();
      $select.append(new Option("Select Country", "", true, true));
      countryOptions.forEach((option) => {
        $select.append(new Option(option.text, option.id, false, false));
      });

      $select.select2({
        width: "100%",
        placeholder: "Select Country",
        allowClear: true,
        minimumResultsForSearch: 0,
      });

      $select.on("change", function (e) {
        setFilters((prev) => ({ ...prev, country: e.target.value || "" }));
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      if (countrySelectRef.current) {
        const $select = $(countrySelectRef.current);
        if ($select.data("select2")) {
          $select.select2("destroy");
        }
      }
    };
  }, [filterExpanded, countryOptions]);

  // Initialize Select2 for Zone (copied from /dashboard)
  useEffect(() => {
    if (!filterExpanded || !zoneSelectRef.current || zoneOptions.length === 0) return;
    const $select = $(zoneSelectRef.current);
    if (!$select.length) return;

    if ($select.data("select2")) {
      $select.select2("destroy");
    }
    $select.empty();
    $select.append(new Option("Select Zone", "", true, true));
    zoneOptions.forEach((option) => {
      $select.append(new Option(option.text, option.id, false, false));
    });
    $select.select2({
      width: "100%",
      placeholder: "Select Zone",
      allowClear: true,
      minimumResultsForSearch: 0,
    });

    const $container = $select.next(".select2-container");
    const $arrow = $container.find(".select2-selection__arrow");
    if ($arrow.length === 0 || $arrow.find("b").length === 0) {
      $arrow.html('<b role="presentation"></b>');
    }
    $arrow.css({
      height: "42px",
      right: "12px",
      width: "20px",
      display: "block",
      position: "relative",
    });
    $arrow.find("b").css({
      borderColor: "#888 transparent transparent transparent",
      borderStyle: "solid",
      borderWidth: "5px 4px 0 4px",
      height: "0",
      left: "50%",
      marginLeft: "-4px",
      marginTop: "-2px",
      position: "absolute",
      top: "50%",
      width: "0",
      display: "block",
    });

    $select.on("select2:open", function () {
      const $openArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      $openArrow.css({
        borderColor: "transparent transparent #888 transparent",
        borderWidth: "0 4px 5px 4px",
      });
    });

    $select.on("select2:close", function () {
      const $closeArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      if ($closeArrow.length) {
        $closeArrow.css({
          borderColor: "#888 transparent transparent transparent",
          borderWidth: "5px 4px 0 4px",
        });
      }
    });

    $select.on("change", function (e) {
      setFilters((prev) => ({ ...prev, zone: e.target.value || "" }));
    });

    return () => {
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
    };
  }, [filterExpanded, zoneOptions]);

  // Initialize Select2 for Region (copied from /dashboard)
  useEffect(() => {
    if (!filterExpanded || !regionSelectRef.current || regionOptions.length === 0) return;
    const $select = $(regionSelectRef.current);
    if (!$select.length) return;

    if ($select.data("select2")) {
      $select.select2("destroy");
    }

    $select.empty();
    $select.append(new Option("Select Region", "", true, true));
    regionOptions.forEach((option) => {
      $select.append(new Option(option.text, option.id, false, false));
    });

    $select.select2({
      width: "100%",
      placeholder: "Select Region",
      allowClear: true,
      minimumResultsForSearch: 0,
    });

    setTimeout(() => {
      const $container = $select.next(".select2-container");
      if ($container.length) {
        const $arrow = $container.find(".select2-selection__arrow");
        if ($arrow.length === 0 || $arrow.find("b").length === 0) {
          $arrow.html('<b role="presentation"></b>');
        }
        $arrow.css({
          height: "42px",
          right: "12px",
          width: "20px",
          display: "block",
          position: "relative",
        });
        const $arrowB = $arrow.find("b");
        if ($arrowB.length) {
          $arrowB.css({
            borderColor: "#888 transparent transparent transparent",
            borderStyle: "solid",
            borderWidth: "5px 4px 0 4px",
            height: "0",
            left: "50%",
            marginLeft: "-4px",
            marginTop: "-2px",
            position: "absolute",
            top: "50%",
            width: "0",
            display: "block",
          });
        }
      }
    }, 100);

    $select.on("select2:open", function () {
      const $openArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      if ($openArrow.length) {
        $openArrow.css({
          borderColor: "transparent transparent #888 transparent",
          borderWidth: "0 4px 5px 4px",
        });
      }
    });

    $select.on("select2:close", function () {
      const $closeArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      if ($closeArrow.length) {
        $closeArrow.css({
          borderColor: "#888 transparent transparent transparent",
          borderWidth: "5px 4px 0 4px",
        });
      }
    });

    $select.on("change", function (e) {
      setFilters((prev) => ({ ...prev, region: e.target.value || "" }));
    });

    return () => {
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
    };
  }, [filterExpanded, regionOptions]);

  // Initialize Select2 for Territory (copied from /dashboard)
  useEffect(() => {
    if (!filterExpanded || !territorySelectRef.current || territoryOptions.length === 0)
      return;
    const $select = $(territorySelectRef.current);
    if (!$select.length) return;
    if ($select.data("select2")) {
      $select.select2("destroy");
    }
    $select.empty();
    $select.append(new Option("Select Territory", "", true, true));
    territoryOptions.forEach((option) => {
      $select.append(new Option(option.text, option.id, false, false));
    });

    $select.select2({
      width: "100%",
      placeholder: "Select Territory",
      allowClear: true,
      minimumResultsForSearch: 0,
    });

    setTimeout(() => {
      const $container = $select.next(".select2-container");
      if ($container.length) {
        const $arrow = $container.find(".select2-selection__arrow");
        if ($arrow.length === 0 || $arrow.find("b").length === 0) {
          $arrow.html('<b role="presentation"></b>');
        }
        $arrow.css({
          height: "42px",
          right: "12px",
          width: "20px",
          display: "block",
          position: "relative",
        });
        const $arrowB = $arrow.find("b");
        if ($arrowB.length) {
          $arrowB.css({
            borderColor: "#888 transparent transparent transparent",
            borderStyle: "solid",
            borderWidth: "5px 4px 0 4px",
            height: "0",
            left: "50%",
            marginLeft: "-4px",
            marginTop: "-2px",
            position: "absolute",
            top: "50%",
            width: "0",
            display: "block",
          });
        }
      }
    }, 100);

    $select.on("select2:open", function () {
      const $openArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      if ($openArrow.length) {
        $openArrow.css({
          borderColor: "transparent transparent #888 transparent",
          borderWidth: "0 4px 5px 4px",
        });
      }
    });

    $select.on("select2:close", function () {
      const $closeArrow = $select
        .next(".select2-container")
        .find(".select2-selection__arrow b");
      if ($closeArrow.length) {
        $closeArrow.css({
          borderColor: "#888 transparent transparent transparent",
          borderWidth: "5px 4px 0 4px",
        });
      }
    });

    $select.on("change", function (e) {
      setFilters((prev) => ({ ...prev, territory: e.target.value || "" }));
    });

    return () => {
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
    };
  }, [filterExpanded, territoryOptions]);

  // Handle filter button click (same as /dashboard)
  const handleFilter = () => {
    loadData();
  };

  const handleReset = () => {
    const fy = getCurrentFinancialYearRange();
    setFilters({
      dateRange: fy.dateRange,
      dateRangeStart: fy.dateRangeStart,
      dateRangeEnd: fy.dateRangeEnd,
      status: [],
      bv: [],
      invoiceNo: "",
      country: "",
      zone: "",
      region: "",
      territory: "",
    });

    if (filterDateRangePickerRef.current) {
      $(filterDateRangePickerRef.current).val(fy.dateRange);
    }

    if (countrySelectRef.current) {
      $(countrySelectRef.current).val(null).trigger("change");
    }
    if (zoneSelectRef.current) {
      $(zoneSelectRef.current).val(null).trigger("change");
    }
    if (regionSelectRef.current) {
      $(regionSelectRef.current).val(null).trigger("change");
    }
    if (territorySelectRef.current) {
      $(territorySelectRef.current).val(null).trigger("change");
    }

    setShowInvoiceSuggestions(false);
    setInvoiceSuggestions([]);

    loadData({
      fromDate: fy.dateRangeStart,
      toDate: fy.dateRangeEnd,
      status: [],
      bv: [],
      country: "",
      zone: "",
      region: "",
      territory: "",
      invoiceNo: "",
    });
  };

  const palette = [
    "#4a90e2",
    "#50c878",
    "#ff9800",
    "#17a2b8",
    "#9c27b0",
    "#f44336",
    "#00bcd4",
    "#ffeb3b",
    "#795548",
    "#607d8b",
  ];

  const userTypeChartLabels = (Array.isArray(userTypeRows) ? userTypeRows : []).map((r) => {
    const raw =
      r?.userType ??
      r?.UserType ??
      r?.userTypeName ??
      r?.UserTypeName ??
      "—";
    const label = String(raw ?? "").trim();
    // If backend sends placeholder/junk values, don't render them as labels.
    if (!label || label === "/" || label === "-" || label.toLowerCase() === "null") return "—";
    return label;
  });
  const userTypeChartValues = (Array.isArray(userTypeRows) ? userTypeRows : []).map((r) =>
    Math.round(Number(r.avgTatDays ?? r.AvgTatDays ?? 0))
  );
  const userTypeMaxVal = Math.max(0, ...userTypeChartValues);
  const userTypeYMax =
    userTypeMaxVal === 0
      ? 5
      : Math.ceil(userTypeMaxVal * 1.2) <= 10
        ? Math.ceil(userTypeMaxVal * 1.2)
        : Math.ceil((userTypeMaxVal * 1.2) / 5) * 5;
  const userTypeStep = userTypeYMax <= 10 ? 1 : userTypeYMax <= 50 ? 5 : 10;

  const userTypeBarData = {
    labels: userTypeChartLabels,
    datasets: [
      {
        label: "Avg. TAT (Days)",
        data: userTypeChartValues,
        backgroundColor: userTypeChartLabels.map((_, i) => palette[i % palette.length]),
        borderColor: userTypeChartLabels.map((_, i) => palette[i % palette.length]),
        borderWidth: 1,
        // Match spacing feel of /csrp/dashboard "SR Category breakup"
        barPercentage: 0.5,
        categoryPercentage: userTypeChartLabels.length <= 1 ? 0.4 : 0.7,
        barThickness: 30,
      },
    ],
  };

  const userTypeBarOptions = {
    responsive: true,
    // Important: allow Chart.js to use wrapper height so rotated x-ticks don't get clipped.
    maintainAspectRatio: false,
    layout: {
      // Keep bottom padding tight; labels will wrap instead of rotate.
      padding: { bottom: 30 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `Avg. TAT: ${context.parsed.y} days`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        max: userTypeYMax,
        suggestedMax: userTypeYMax,
        ticks: {
          stepSize: userTypeStep,
          precision: 0,
          maxTicksLimit: 11,
        },
        grid: { color: "rgba(0, 0, 0, 0.1)" },
      },
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          padding: 8,
          font: { size: 11, weight: "600" },
          crossAlign: "near",
          callback: function (value, index) {
            // Force-bind ticks to computed label array (prevents unexpected tick values like "/" rendering)
            const raw = String(userTypeChartLabels?.[index] ?? value ?? "").trim();
            if (!raw) return "";

            // Wrap role names to multiple lines so they don't get cut off.
            const maxLineLen = 14;
            const maxLines = 3;
            const parts = raw.split(/\s+/).filter(Boolean);
            const lines = [];
            let cur = "";
            for (const p of parts) {
              const next = cur ? `${cur} ${p}` : p;
              if (next.length <= maxLineLen) {
                cur = next;
              } else {
                if (cur) lines.push(cur);
                cur = p;
              }
              if (lines.length >= maxLines) break;
            }
            if (lines.length < maxLines && cur) lines.push(cur);
            if (lines.length > maxLines) lines.length = maxLines;
            return lines.length > 1 ? lines : lines[0];
          },
        },
        grid: { display: false },
      },
    },
  };

  // Normalize/merge categories (SP can return duplicates like "Doors" twice).
  const normalizedCategoryRows = React.useMemo(() => {
    const rows = Array.isArray(categoryRows) ? categoryRows : [];
    const map = new Map();
    rows.forEach((r) => {
      const name = (r.category ?? r.Category ?? "—").toString().trim() || "—";
      const count = Number(r.claimCount ?? r.ClaimCount ?? 0) || 0;
      const avg = Number(r.avgTatDays ?? r.AvgTatDays ?? 0) || 0;
      const cur = map.get(name) || { category: name, claimCount: 0, sumTat: 0 };
      cur.claimCount += count;
      cur.sumTat += avg * count; // weighted sum
      map.set(name, cur);
    });
    return Array.from(map.values()).map((g) => ({
      category: g.category,
      claimCount: g.claimCount,
      avgTatDays: g.claimCount ? g.sumTat / g.claimCount : 0,
    }));
  }, [categoryRows]);

  const categoryChartLabels = normalizedCategoryRows.map((r) => r.category ?? "—");
  const categoryAvgTatValues = normalizedCategoryRows.map((r) =>
    Math.round(Number(r.avgTatDays ?? 0))
  );
  const categoryCountValues = normalizedCategoryRows.map((r) => Number(r.claimCount ?? 0));

  const getCategoryExportRows = () =>
    normalizedCategoryRows.map((r) => ({
      Category: r.category ?? "—",
      "Claim Count": Number(r.claimCount ?? 0),
      "Avg. TAT (Days)": Number(r.avgTatDays ?? 0).toFixed(2),
    }));
  const categoryDonutData = {
    labels: categoryChartLabels,
    datasets: [
      {
        label: "Claim Count",
        data: categoryCountValues,
        backgroundColor: categoryChartLabels.map((_, i) => {
          return palette[i % palette.length];
        }),
        borderColor: categoryChartLabels.map((_, i) => {
          return palette[i % palette.length];
        }),
        borderWidth: 1,
      },
    ],
  };

  const categoryDonutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: { padding: { top: 4, right: 12, bottom: 4, left: 12 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || "";
            const idx = context.dataIndex;
            const count = categoryCountValues[idx] ?? 0;
            const avgDays = categoryAvgTatValues[idx] ?? 0;
            return `${label}: ${count} claims · Avg TAT ${avgDays} days`;
          },
        },
      },
    },
    // Feed the plugin the real (integer) count values so labels show correct counts.
    pieChartValuePlugin: {
      displayValues: categoryCountValues,
    },
    cutout: "60%",
  };

  const getUserTypeExportRows = () =>
    (Array.isArray(userTypeRows) ? userTypeRows : []).map((r) => ({
      "User Type": r.userType ?? r.UserType ?? "—",
      "Claim Count": Number(r.claimCount ?? r.ClaimCount ?? 0),
      "Avg. TAT (Days)": Number(r.avgTatDays ?? r.AvgTatDays ?? 0).toFixed(2),
    }));

  return (
    <div className="dashboard-container">
      <div className="container-fluid">
        {/* Filter Card (same UX as /dashboard) */}
        <div className="row mb-3">
          <div className="col-12">
            <div
              className="card-panel mb-3 filter-panel"
              style={{ paddingBottom: filterExpanded ? "15px" : "2px" }}
            >
              <div
                className="section-heading d-flex align-items-center gap-2 mb-3"
                style={{ marginBottom: "14px", cursor: "pointer" }}
                onClick={() => setFilterExpanded(!filterExpanded)}
              >
                <FilterListIcon
                  fontSize="small"
                  className="text-muted"
                  style={{ width: "18px", height: "18px", flexShrink: 0, marginTop: "2px" }}
                />
                <span>Filter</span>
                {filterExpanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    className="text-muted"
                    style={{ width: "18px", height: "18px", flexShrink: 0, marginLeft: "auto" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    className="text-muted"
                    style={{ width: "18px", height: "18px", flexShrink: 0, marginLeft: "auto" }}
                  />
                )}
              </div>

              {filterExpanded && (
                <div className="container-fluid">
                  <div className="row g-3 align-items-end">
                    {/* Date Range */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Select Date Range
                      </label>
                      <div className="input-icon" style={{ position: "relative" }}>
                        <input
                          type="text"
                          ref={filterDateRangePickerRef}
                          className="form-control form-control-light text-start"
                          placeholder="Select date range"
                          readOnly
                          style={{ height: "42px", textAlign: "left" }}
                        />
                        <CalendarTodayIcon
                          className="icon-right"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            width: "16px",
                            height: "16px",
                            color: "#6c757d",
                          }}
                        />
                      </div>
                    </div>

                    {/* Status */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Status</label>
                      <select
                        ref={statusSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left", display: "none" }}
                        multiple
                      >
                        {statusOptions.length > 0
                          ? statusOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.text}
                              </option>
                            ))
                          : null}
                      </select>
                      <CustomMultiSelect
                        options={statusOptions}
                        value={filters.status || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            status: Array.isArray(selected) ? selected : [],
                          }))
                        }
                        placeholder="Select Status"
                        disabled={false}
                      />
                    </div>

                    {/* Business Vertical */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Business Vertical
                      </label>
                      <select
                        ref={bvSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left", display: "none" }}
                      >
                        {bvOptions.length > 0
                          ? bvOptions.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.text}
                              </option>
                            ))
                          : null}
                      </select>
                      <CustomMultiSelect
                        options={bvOptions}
                        value={filters.bv || []}
                        onChange={(selected) =>
                          setFilters((prev) => ({
                            ...prev,
                            bv: Array.isArray(selected) ? selected.map((v) => String(v)) : [],
                          }))
                        }
                        placeholder="Select Business Vertical"
                        disabled={false}
                      />
                    </div>

                    {/* Invoice No. */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Invoice No.
                      </label>
                      <div className="input-icon" style={{ position: "relative" }}>
                        <input
                          type="text"
                          className="form-control form-control-light text-start"
                          placeholder="Enter invoice number"
                          value={filters.invoiceNo}
                          onChange={handleInvoiceInputChange}
                          style={{ height: "42px", textAlign: "left" }}
                        />
                        <SearchIcon
                          className="icon-right"
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            pointerEvents: "none",
                            width: "16px",
                            height: "16px",
                            color: "#6c757d",
                          }}
                        />
                        {showInvoiceSuggestions && invoiceSuggestions.length > 0 && (
                          <ul
                            className="invoice-suggestions-list"
                            style={{
                              position: "absolute",
                              top: "100%",
                              left: 0,
                              right: 0,
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              borderRadius: "4px",
                              marginTop: "4px",
                              maxHeight: "200px",
                              overflowY: "auto",
                              zIndex: 1000,
                              listStyle: "none",
                              padding: 0,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                          >
                            {invoiceSuggestions.map((suggestion, index) => (
                              <li
                                key={index}
                                onClick={() => handleInvoiceSuggestionClick(suggestion)}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  borderBottom: "1px solid #eee",
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = "white";
                                }}
                              >
                                {suggestion}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {/* Country */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Country
                      </label>
                      <select
                        ref={countrySelectRef}
                        className="form-select form-control-light filter-select text-start"
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        <option value="">Select Country</option>
                      </select>
                    </div>

                    {/* Zone */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">Zone</label>
                      <select
                        ref={zoneSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        disabled={!filters.country}
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        <option value="">Select Zone</option>
                      </select>
                    </div>

                    {/* Region */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Region
                      </label>
                      <select
                        ref={regionSelectRef}
                        className="form-select form-control-light filter-select text-start"
                        disabled={!filters.zone}
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        <option value="">Select Region</option>
                      </select>
                    </div>

                    {/* Territory */}
                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1 text-start">
                        Territory
                      </label>
                      <select
                        ref={territorySelectRef}
                        className="form-select form-control-light filter-select text-start"
                        disabled={!filters.region}
                        style={{ height: "42px", textAlign: "left" }}
                      >
                        <option value="">Select Territory</option>
                      </select>
                    </div>

                    <div className="col-12 col-md-6 col-lg-3">
                      <label className="input-label d-block mb-1" style={{ visibility: "hidden" }}>
                        Actions
                      </label>
                      <div className="d-flex gap-2 filter-buttons">
                        <button
                          type="button"
                          className="btn btn-filter"
                          onClick={handleFilter}
                          style={{
                            background: "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                            borderRadius: "4px",
                            padding: "8px 24px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <FilterListIcon fontSize="small" className="me-1" />
                          Filter
                        </button>
                        <button
                          type="button"
                          className="btn btn-reset-filter"
                          onClick={handleReset}
                          title="Reset"
                          style={{
                            background: "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 600,
                            boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                            borderRadius: "4px",
                            padding: "8px 16px",
                            transition: "all 0.3s ease",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <RestartAltIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI Cards (dashboard-style layout/classes; text + bindings unchanged) */}
        <div className="row" style={{ display: "none" }}>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card total">
              <div className="sr-count-label">Total Claims</div>
              <div className="sr-count-subtitle">Matching current filters</div>
              <div className="sr-count-value">{kpis.totalClaims}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card special">
              <div className="sr-count-label">Avg. Grand TAT</div>
              <div className="sr-count-subtitle">Across all levels</div>
              <div className="sr-count-value">
                {Math.round(Number(kpis.avgGrandTatDays || 0))}
                <span style={{ fontSize: 14, fontWeight: 500, marginLeft: 6, opacity: 0.7 }}>
                  days
                </span>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card closed">
              <div className="sr-count-label">Approved</div>
              <div className="sr-count-subtitle">Approved claims count</div>
              <div className="sr-count-value">{kpis.approvedClaims}</div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-3 mb-3">
            <div className="sr-count-card open">
              <div className="sr-count-label">Pending</div>
              <div className="sr-count-subtitle">Requires action</div>
              <div className="sr-count-value">{kpis.pendingClaims}</div>
            </div>
          </div>
        </div>

        {/* Complaint table */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="card-panel claim-portal-table-panel">
              <div className="claim-portal-dt">
                <table
                  ref={complaintTableRef}
                  className="claim-portal-table table table-striped table-bordered complaint-table custmTblLst"
                >
                  <thead>
                    <tr>
                      <th>SL No</th>
                      <th>Sr No</th>
                      <th>FY</th>
                      <th>Status</th>
                      <th>Plant</th>
                      <th>Zone</th>
                      <th>Business Vertical</th>
                      <th>Sr Category</th>
                      <th>TAT SE</th>
                      <th>TAT BCM</th>
                      <th>TAT Zonal Head</th>
                      <th>TAT Dispatch</th>
                      <th>TAT CPD Head</th>
                      <th>TAT Quality</th>
                      <th>TAT Mfg. Head</th>
                      <th>TAT Country Head</th>
                      <th>TAT MD</th>
                      <th>TAT Export Comm.</th>
                      <th>TAT Exp. Contr.</th>
                      <th>TAT Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(complaintRows) && complaintRows.length > 0 ? (
                      complaintRows.map((r, idx) => {
                        const get = (k) => getComplaintRowValue(r, k);
                        const status = get("status") || "";
                        const normalizedStatus = String(status).trim().toLowerCase();
                        let statusColor = "#000000";
                        if (normalizedStatus === "open") {
                          statusColor = "#1976d2";
                        } else if (normalizedStatus === "rejected") {
                          statusColor = "#ff0000";
                        } else if (
                          normalizedStatus === "pending" ||
                          normalizedStatus.includes("pending")
                        ) {
                          statusColor = "#ff9800";
                        } else if (
                          normalizedStatus === "approved" ||
                          normalizedStatus === "reopen" ||
                          normalizedStatus === "closed"
                        ) {
                          statusColor = "#4caf50";
                        }

                        const childSrCode =
                          get("childSrCode") ||
                          get("childSrNo") ||
                          get("complaintNo") ||
                          "";
                        const parentSrCode =
                          get("srCode") ||
                          get("parentSrCode") ||
                          deriveParentFromChildSr(childSrCode) ||
                          "";
                        const srNo =
                          childSrCode ||
                          get("srNo") ||
                          get("srNumber") ||
                          parentSrCode ||
                          "—";

                        return (
                          <tr key={idx}>
                            <td className="text-center">{idx + 1}</td>
                            <td style={{ fontWeight: 700, color: "#0f00f5" }}>
                              {srNo && srNo !== "—" ? (
                                (parentSrCode || childSrCode) ? (
                                <a
                                  href="#"
                                  className="sr-link"
                                  data-parent-sr-code={parentSrCode}
                                  data-child-sr-code={childSrCode}
                                >
                                  {srNo}
                                </a>
                                ) : (
                                  <span>{srNo}</span>
                                )
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>{get("financialYear") || "—"}</td>
                            <td>
                              <span style={{ color: statusColor, fontWeight: 600 }}>
                                {String(status || "—")}
                              </span>
                            </td>
                            <td>{get("plant") || "—"}</td>
                            <td>{get("zone") || "—"}</td>
                            <td>{get("category") || "—"}</td>
                            <td>{get("subCategory") || "—"}</td>

                            <td><span className={`tat-chip ${tatClass(get("tatSe"))}`}>{get("tatSe") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatBcm"))}`}>{get("tatBcm") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatZonalHead"))}`}>{get("tatZonalHead") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatDispatch"))}`}>{get("tatDispatch") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatCpdHead"))}`}>{get("tatCpdHead") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatQuality"))}`}>{get("tatQuality") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatMfgHead"))}`}>{get("tatMfgHead") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatCountryHead"))}`}>{get("tatCountryHead") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatMd"))}`}>{get("tatMd") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatExportComm"))}`}>{get("tatExportComm") ?? 0}</span></td>
                            <td><span className={`tat-chip ${tatClass(get("tatExportContr"))}`}>{get("tatExportContr") ?? 0}</span></td>
                            <td style={{ fontWeight: 800 }}>
                              <span className={`tat-chip ${tatClass(get("tatGrandTotal"))}`}>
                                {get("tatGrandTotal") ?? 0}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : null}
                  </tbody>
                </table>
                {(!Array.isArray(complaintRows) || complaintRows.length === 0) && (
                  <div style={{ padding: 18, textAlign: "center", color: "#6c757d", fontWeight: 600 }}>
                    No records match the current filters.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Zone-wise controlling dashboard (split): Category donut + User Type bar */}
        <div className="row charts-row mt-0">
          {/* Donut: Category breakup */}
          <div className="col-12 mb-3">
            <div className="pie-chart-widget cpd-donut-split">
              <div className="pie-chart-header">
                <h2 className="pie-chart-title">Business Vertical Wise Avg. TAT (Days)</h2>
                <div className="cpd-export-wrap">
                  <button className="export-button cpd-export-trigger" type="button" title="Export">
                    <svg
                      className="export-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span>Export</span>
                    <span className="cpd-export-caret">▾</span>
                  </button>
                  <ul className="cpd-export-menu">
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={async () => {
                          const rows = getCategoryExportRows();
                          await exportJsonToExcel(
                            rows,
                            `business-vertical-wise-avg-tat-${formatDateDDMMYYYY()}.xlsx`,
                            "Business Vertical Wise Avg. TAT (Days)"
                          );
                        }}
                      >
                        Excel
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          exportRowsToPdf(
                            getCategoryExportRows(),
                            `business-vertical-wise-avg-tat-${formatDateDDMMYYYY()}.pdf`,
                            "portrait"
                          );
                        }}
                      >
                        PDF
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          exportRowsToCsv(
                            getCategoryExportRows(),
                            `business-vertical-wise-avg-tat-${formatDateDDMMYYYY()}.csv`
                          );
                        }}
                      >
                        CSV
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="pie-chart-container">
                <div className="pie-chart-wrapper">
                  <Pie data={categoryDonutData} options={categoryDonutOptions} />
                </div>
                <div className="pie-chart-legend">
                  {categoryDonutData.labels.map((label, index) => (
                    <div key={index} className="legend-item">
                      <span
                        className="legend-color"
                        style={{
                          backgroundColor: palette[index % palette.length],
                          border: "1px solid rgba(0,0,0,0.10)",
                        }}
                      />
                      <span className="legend-label">{label}</span>
                      <span className="legend-value">
                        {categoryCountValues[index] ?? 0} · {categoryAvgTatValues[index] ?? 0}d
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {(!Array.isArray(categoryRows) || categoryRows.length === 0) && (
                <div style={{ padding: 18, textAlign: "center", color: "#6c757d", fontWeight: 600 }}>
                  No category breakup available for the current filters.
                </div>
              )}
            </div>
          </div>

          {/* Bar: User type breakup */}
          <div className="col-12 mb-3">
            <div className="bar-chart-widget">
              <div className="bar-chart-header">
                <h2 className="bar-chart-title">User Role Wise Avg. TAT (Days)</h2>
                <DashboardExportDropdown
                  onExcel={async () => {
                    const rows = getUserTypeExportRows();
                    await exportJsonToExcel(
                      rows,
                      `user-role-wise-avg-tat-${formatDateDDMMYYYY()}.xlsx`,
                      "User Role Wise Avg. TAT (Days)"
                    );
                  }}
                  onPdf={() => {
                    exportRowsToPdf(
                      getUserTypeExportRows(),
                      `user-role-wise-avg-tat-${formatDateDDMMYYYY()}.pdf`,
                      "portrait"
                    );
                  }}
                  onCsv={() => {
                    exportRowsToCsv(
                      getUserTypeExportRows(),
                      `user-role-wise-avg-tat-${formatDateDDMMYYYY()}.csv`
                    );
                  }}
                />
              </div>
              {/* Match /csrp/dashboard structure */}
              <div className="bar-chart-wrapper">
                <Bar data={userTypeBarData} options={userTypeBarOptions} />
              </div>
              {(!Array.isArray(userTypeRows) || userTypeRows.length === 0) && (
                <div style={{ padding: 18, textAlign: "center", color: "#6c757d", fontWeight: 600 }}>
                  No user type breakup available for the current filters.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimPortalDashboard;

